import type { Event, EventInterest, Group, GroupEvent } from '../types'

/** One event in the Crew activity feed, with everything the crew did on it merged. */
export interface CrewActivity {
  key: string
  event: Event
  /** Newest first. */
  goingIds: string[]
  /** Newest first. */
  interestedIds: string[]
  /** The viewer's groups it was linked to, one per group, newest first. */
  groups: { group: Group; userId: string; at: number }[]
  /** Newest of all the above. */
  at: number
}

export const millis = (t?: { toMillis(): number } | null) => (t ? t.toMillis() : 0)

type Draft = { event: Event; going: [string, number][]; interested: [string, number][]; groups: Map<string, CrewActivity['groups'][number]> }

const newestFirst = (list: [string, number][]) => list.sort((a, b) => b[1] - a[1]).map(([uid]) => uid)

/**
 * Groups recent RSVPs and group links into one item per event, so a crew that
 * shares 2 groups with the viewer sees an event once rather than once per
 * status and per group. `eventsById` is already narrowed to the events the
 * feed may show; anything else is dropped.
 */
export function buildCrewActivity({ rsvps, links, eventsById, groupsById, viewerUid, max }: {
  rsvps: EventInterest[]
  links: GroupEvent[]
  eventsById: Map<string, Event>
  groupsById: Map<string, Group>
  viewerUid: string | null
  max: number
}): CrewActivity[] {
  const drafts = new Map<string, Draft>()
  const draftFor = (event: Event) => {
    let d = drafts.get(event.id)
    if (!d) drafts.set(event.id, (d = { event, going: [], interested: [], groups: new Map() }))
    return d
  }

  for (const r of rsvps) {
    const event = eventsById.get(r.eventId)
    if (!event || r.userId === viewerUid) continue
    if (r.status !== 'going' && r.status !== 'interested') continue
    draftFor(event)[r.status].push([r.userId, millis(r.updatedAt)])
  }
  for (const ge of links) {
    const event = eventsById.get(ge.eventId)
    const group = groupsById.get(ge.groupId)
    if (!event || !group) continue
    const at = millis(ge.createdAt)
    const groups = draftFor(event).groups
    if ((groups.get(group.id)?.at ?? -1) < at) groups.set(group.id, { group, userId: ge.createdBy, at })
  }

  return [...drafts.values()]
    .map(d => {
      const groups = [...d.groups.values()].sort((a, b) => b.at - a.at)
      const at = Math.max(0, ...d.going.map(g => g[1]), ...d.interested.map(i => i[1]), ...groups.map(g => g.at))
      return {
        key: d.event.id,
        event: d.event,
        goingIds: newestFirst(d.going),
        interestedIds: newestFirst(d.interested),
        groups,
        at,
      }
    })
    .sort((a, b) => b.at - a.at)
    .slice(0, max)
}
